import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  type User,
  type Auth,
  type MultiFactorResolver,
} from 'firebase/auth';
import {
  getAuthInstance,
  getGoogleAuthProvider,
  setAuthPersistence,
  isIdx,
} from '../services/firebase';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  isAuthorized: boolean;
  loginError: string | null;
  mfaRequired: boolean;
  login: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithMfaCode: (otp: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function useAuthProvider(): AuthContextValue {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    async function initAuth() {
      try {
        const authInstance = await getAuthInstance();
        setAuth(authInstance);

        unsubscribe = onAuthStateChanged(authInstance, async (firebaseUser) => {
          console.log('🔄 Auth state changed:', firebaseUser ? firebaseUser.email : 'No user');

          if (firebaseUser) {
            try {
              const tokenResult = await firebaseUser.getIdTokenResult();
              const authTime = new Date(tokenResult.authTime).getTime();
              const tokenAgeHours = (Date.now() - authTime) / (1000 * 60 * 60);
              console.log(`⏰ Token age: ${tokenAgeHours.toFixed(2)} hours`);

              if (tokenAgeHours > 24) {
                console.log('🔒 Token is stale (>24 hours old), forcing logout...');
                await signOut(authInstance);
                setUser(null);
                setIsAuthorized(false);
                setLoginError('Your session has expired. Please log in again.');
                return;
              }

              setUser(firebaseUser);
              setIsAuthorized(true);
              console.log('✅ User signed in:', firebaseUser.email);
              document.dispatchEvent(new Event('firebaseReady'));
            } catch (error) {
              console.error('Error checking token age:', error);
              setUser(firebaseUser);
              setIsAuthorized(true);
            }
          } else {
            setUser(null);
            setIsAuthorized(false);
          }
          setLoading(false);
        });

        console.log('🔍 Checking for redirect result...');
        getRedirectResult(authInstance)
          .then((result) => {
            if (result && result.user) {
              console.log('✅ Redirect login success:', result.user.email);
              setLoginError(null);
            } else {
              console.log('ℹ️ No redirect result found (normal page load or cancelled).');
            }
          })
          .catch((redirectError: { message?: string; code?: string }) => {
            console.error('❌ Redirect login error:', redirectError);
            let displayMessage = 'Login failed.';
            const msg = redirectError.message || '';
            const match = msg.match(/"message":"([^"]+)"/);
            if (match && match[1]) {
              displayMessage = match[1];
            } else if (msg.includes('Access denied')) {
              displayMessage = 'Access denied: You are not on the whitelist.';
            } else if (msg.includes('blocking function') || redirectError.code === 'auth/blocking-function-error-response') {
              displayMessage = 'Access Restricted';
            } else {
              displayMessage = msg.replace(/^Firebase: /, '').replace(/\s\(auth\/.*\)\.?$/, '');
            }
            setLoginError(displayMessage);
          });

      } catch (error) {
        console.error('Auth initialization error:', error);
        setLoading(false);
      }
    }

    void initAuth();
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const login = async () => {
    setLoginError(null);
    try {
      let authInstance = auth;
      if (!authInstance) {
        console.log('Auth instance not found, initializing...');
        authInstance = await getAuthInstance();
        setAuth(authInstance);
        await new Promise<void>(resolve => setTimeout(resolve, 100));
      }

      const resolvedAuth = authInstance ?? await getAuthInstance();
      await setAuthPersistence();
      const provider = getGoogleAuthProvider();

      if (isIdx) {
        console.log("IDX environment detected. Using 'signInWithRedirect'.");
        await signInWithRedirect(resolvedAuth, provider);
      } else {
        console.log("Production/local environment detected. Using 'signInWithPopup'.");
        await signInWithPopup(resolvedAuth, provider);
      }
    } catch (error: unknown) {
      const authError = error as { message?: string; code?: string };
      console.error('Login attempt failed:', authError);
      const code = authError.code;
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        console.log('Login popup was closed or cancelled by the user.');
        return;
      }
      const msg = authError.message || '';
      const match = msg.match(/"message":"([^"]+)"/);
      let displayMessage: string;
      if (match && match[1]) {
        displayMessage = match[1];
      } else if (code === 'auth/blocking-function-error-response') {
        displayMessage = 'Access Restricted. Your account is not permitted.';
      } else {
        displayMessage = msg.replace(/^Firebase: /, '').replace(/\s\(auth\/.*\)\.?$/, '');
      }
      setLoginError(displayMessage);
    }
  };

  const loginWithEmail = async (email: string, password: string): Promise<void> => {
    setLoginError(null);
    setMfaRequired(false);
    setMfaResolver(null);
    try {
      const authInstance = auth || await getAuthInstance();
      await signInWithEmailAndPassword(authInstance, email, password);
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string };
      if (authError.code === 'auth/multi-factor-auth-required') {
        const authInstance = auth || await getAuthInstance();
        const resolver = getMultiFactorResolver(authInstance, error as Parameters<typeof getMultiFactorResolver>[1]);
        setMfaResolver(resolver);
        try {
          const recaptchaVerifier = new RecaptchaVerifier(authInstance, 'recaptcha-container', { size: 'invisible' });
          const phoneAuthProvider = new PhoneAuthProvider(authInstance);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            { multiFactorHint: resolver.hints[0], session: resolver.session },
            recaptchaVerifier
          );
          setMfaVerificationId(verificationId);
          setMfaRequired(true);
        } catch (smsError: unknown) {
          console.error('Failed to send MFA SMS:', smsError);
          const msg = (smsError as { message?: string }).message || '';
          setLoginError(msg.replace(/^Firebase: /, '').replace(/\s\(auth\/.*\)\.?$/, '') || 'Failed to send verification code.');
        }
        return;
      }
      const msg = authError.message || '';
      const match = msg.match(/"message":"([^"]+)"/);
      let displayMessage: string;
      if (match && match[1]) {
        displayMessage = match[1];
      } else if (authError.code === 'auth/blocking-function-error-response') {
        displayMessage = 'Access denied. Your account is not permitted.';
      } else if (
        authError.code === 'auth/user-not-found' ||
        authError.code === 'auth/wrong-password' ||
        authError.code === 'auth/invalid-credential'
      ) {
        displayMessage = 'Incorrect email or password.';
      } else {
        displayMessage = msg.replace(/^Firebase: /, '').replace(/\s\(auth\/.*\)\.?$/, '');
      }
      setLoginError(displayMessage);
    }
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    const authInstance = auth || await getAuthInstance();
    const continueUrl = `https://${import.meta.env.VITE_FIREBASE_PROJECT_ID}.web.app/customer`;
    await sendPasswordResetEmail(authInstance, email, { url: continueUrl });
  };

  const loginWithMfaCode = async (otp: string): Promise<void> => {
    setLoginError(null);
    if (!mfaResolver || !mfaVerificationId) return;
    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, otp);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await mfaResolver.resolveSignIn(assertion);
      setMfaRequired(false);
      setMfaResolver(null);
      setMfaVerificationId(null);
    } catch (error: unknown) {
      const msg = (error as { message?: string }).message || '';
      setLoginError(msg.replace(/^Firebase: /, '').replace(/\s\(auth\/.*\)\.?$/, ''));
    }
  };

  const logout = async () => {
    try {
      if (auth) {
        const isCustomerPath = window.location.pathname.startsWith('/customer');
        await signOut(auth);
        window.location.href = isCustomerPath ? '/customer' : '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    return user.getIdToken();
  }, [user]);

  return { user, loading, isAuthorized, loginError, mfaRequired, login, loginWithEmail, loginWithMfaCode, sendPasswordReset, logout, getAuthToken };
}
